import { getAuth, onAuthStateChanged, User } from "firebase/auth";
import { createEmitter } from "ts_utils";
import { AppUser } from "../data/AppUser";
import { UserDTO } from "../data/dtos";
import { UserRole } from "../data/UserRole";
import { FbAuth } from "../services/firebase/FbAuth";
import { Services } from "../services/Services";
import { CreateAccountView, CreateAccountViewEvent, CreateAccountViewEventType }
	from "./ui/CreateAccountView";
import { EmailConfirmationView, EmailConfirmationViewEvent }
	from "./ui/EmailConfirmationView";
import { FinishAccountView, FinishAccountViewEvent }
	from "./ui/FinishAccountView";
import { ManageAccountView, ManageAccountViewEvent }
	from "./ui/ManageAccountView";
import { SignInView, SignInViewEvent } from "./ui/SignInView";
import * as uih from "./ui/UiHelper";

let _ref:UserManager;

export class UserManager {
	private _currentUser:AppUser;
	private _communityMembers:Array<UserDTO>;
	
	private _ui:HTMLDivElement;
	private _signInView:SignInView;
	private _createAccountView:CreateAccountView;
	private _emailConfirmationView:EmailConfirmationView;
	private _finishAccountView:FinishAccountView;
	private _manageAccountView:ManageAccountView;
	
	public get initialSignInStatusRetrieved():boolean {
		return this._initialSignInStatusRetrieved;
	}
	private _initialSignInStatusRetrieved:boolean = false;
	
	private _isActive:boolean = false;
	private _skipManageAccountView:boolean;
	
	private _sessionFinishedResolve: (appUser:AppUser) => void;
	
	public get event() { return this._emitter.event; }
	private _emitter = createEmitter<UserManagerEvent>();
	
	public static SHOW_DEBUG_MESSAGES:boolean = false;
	
	private constructor(ui:HTMLDivElement){
		_ref = this;
		if (ui == null){
			throw 'You must pass the ui HTMLDivElement on the first call to ' +
				'UserManager.get()';
		}
		this._ui = ui;
		this._init();
	}
	
	public static get(ui?:HTMLDivElement):UserManager {
		if (_ref == null){
			new UserManager(ui);
		}
		return _ref;
	}
	
	private _init():void {
		// FirebaseServices.setAuthPersistence(fb.Persistence.NONE);
		onAuthStateChanged(getAuth(), this._onAuthStateChanged);
		
		this._signInView = new SignInView(
			this._ui.querySelector("#act-signInView"));
		this._createAccountView = new CreateAccountView(
			this._ui.querySelector("#act-createAccountView"));
		this._emailConfirmationView = new EmailConfirmationView(
			this._ui.querySelector("#act-emailConfirmationView"));
		this._finishAccountView = new FinishAccountView(
			this._ui.querySelector("#act-finishAccountView"));
		this._manageAccountView = new ManageAccountView(
			this._ui.querySelector("#act-manageAccountView"));
		
		this.hideLoadingOverlay();
		
		this._signInView.event.on(this._onSignInViewEvent);
		this._createAccountView.event.on(this._onCreateAccountViewEvent);
		this._emailConfirmationView.event.on(
			this._onEmailConfirmationViewEvent);
		this._finishAccountView.event.on(this._onFinishAccountViewEvent);
		this._manageAccountView.event.on(this._onManageAccountViewEvent);
		
		this.deactivate();
	}
	
	public show():void {
		uih.show(this._ui);
	}
	
	public hide():void {
		uih.hide(this._ui);
	}
	
	/**loads and activates the appropriate account view state. This does not
	 * display the UI - use [show] for that. Returns a [Future] that completes
	 * when the user closes the ManageAccountView (after fully signing in), or
	 * if [skipManageAccountView] is true, completes as soon as the user fully
	 * signs in. A [UserManagerEventType.sessionFinished] [UserManagerEvent] is
	 * also dispatched at the same time.*/
	public activate(skipManageAccountView:boolean):Promise<AppUser> {
		this._skipManageAccountView = skipManageAccountView;
		
		return new Promise<AppUser>((resolve, reject) => {
			if (!this._isActive){
				this._sessionFinishedResolve = resolve;
				this._updateView();
				this._isActive = true;
			}
			else {
				resolve(this._currentUser);
			}
		});
	}
	
	public activateManageAccount():void {
		if (this._currentUser != null){
			this._manageAccountView.activate(this._currentUser);
		}
	}
	
	/**deactivates and hides each view as well as the loading overlay. The
	 * container is left on the display. Use [hide] to hide it.*/
	public deactivate():void {
		if (this._isActive){
			this._isActive = false;
		
			this._signInView.deactivate();
			this._createAccountView.deactivate();
			this._emailConfirmationView.deactivate();
			this._finishAccountView.deactivate();
			this._manageAccountView.deactivate();
			this.hideLoadingOverlay();
		}
	}
	
	/**logs the user out of the application and Firebase. Does not log the user
	 * out of Google.*/
	public async logout():Promise<void> {
		if (FbAuth.getCurrentUser() != null){
			await FbAuth.logout();
			this._log('fb logout');
		}
		this._currentUser = null;
		this._log('currentUser now null');
	}
	
	public async refreshUserToken():Promise<void> {
		await FbAuth.getUserToken(true);
	}
	
	public showLoadingOverlay():void {
		uih.show(_ref._ui.querySelector(".ani-loadingOverlay"));
	}
	
	public hideLoadingOverlay():void {
		uih.hide(_ref._ui.querySelector(".ani-loadingOverlay"));
	}
	
	private _updateView():void {
		this._signInView.deactivate();
		this._createAccountView.deactivate();
		this._emailConfirmationView.deactivate();
		this._finishAccountView.deactivate();
		this._manageAccountView.deactivate();
		
		if (!this._initialSignInStatusRetrieved){
			this.showLoadingOverlay();
		}
		else if (FbAuth.getCurrentUser() == null){
			this._signInView.activate();
		}
		else if (!FbAuth.emailVerified()){
			this._emailConfirmationView.activate();
		}
		else if (this._currentUser == null){
			this._finishAccountView.activate();
		}
		else {
			if (this._skipManageAccountView){
				this._sendFinishedNotification();
			}
			else {
				this._manageAccountView.activate(this._currentUser);
			}
		}
	}
	
	private _onAuthStateChanged = async (user:User):Promise<void> => {
		this._log(`fb auth changed - fbUser = ${user}`);
		this._currentUser = null;
		if (user != null){
			this._log('contacting backend...');
			this._currentUser = await this._getUserDataFromService();
			this._log(`backend result, currentUser = ${this._currentUser}`);
		}
		this._initialSignInStatusRetrieved = true;
		
		this._emitter.emit(
			new UserManagerEvent(UserManagerEventType.authStateChanged));
		
		if (this._isActive){
			this._updateView();
		}
	}
	
	private _onSignInViewEvent = async (e:SignInViewEvent):Promise<void> => {
		if (e === SignInViewEvent.newAccountRequest){
			this._signInView.deactivate();
			this._createAccountView.activate();
		}
		else if (e === SignInViewEvent.canceled){
			this._sendFinishedNotification();
		}
	}
	
	private _onCreateAccountViewEvent = async (e:CreateAccountViewEvent)
			:Promise<void> => {
		if (e.type === CreateAccountViewEventType.accountCreated){
			await FbAuth.sendEmailVerification();
		}
		else if (e.type === CreateAccountViewEventType.canceled){
			this._createAccountView.deactivate();
			this._signInView.activate();
		}
	}
	
	private _onEmailConfirmationViewEvent = async (e:EmailConfirmationViewEvent)
			:Promise<void> => {
		if (e === EmailConfirmationViewEvent.confirmed){
			this._updateView();
		}
		else if (e === EmailConfirmationViewEvent.signoutRequest){
			await this.logout();
		}
	}
	
	private _onFinishAccountViewEvent = async (e:FinishAccountViewEvent)
			:Promise<void> => {
		if (e === FinishAccountViewEvent.accountFinished){
			this._currentUser = await this._getUserDataFromService();
			this.hideLoadingOverlay();
			this._updateView();
		}
		else if (e === FinishAccountViewEvent.signoutRequest){
			await this.logout();
		}
	}
	
	private _onManageAccountViewEvent = async (e:ManageAccountViewEvent)
			:Promise<void> => {
		switch (e){
			case ManageAccountViewEvent.manageSubscriptionRequest:
				break;
			
			case ManageAccountViewEvent.deleteAccountRequest:
				break;
				
			case ManageAccountViewEvent.closeRequest:
				this._sendFinishedNotification();
				break;
		}
	}
	
	private async _getUserDataFromService():Promise<AppUser> {
		let rdto = await Services.getUserInfo();
		
		if (rdto.success){
			let appUser = new AppUser();
			appUser.updateFromUserDTO(rdto.data);
			return appUser;
		}
		
		return null;
	}
	
	private _sendFinishedNotification():void {
		let ev = new UserManagerEvent(UserManagerEventType.sessionFinished);
		this._emitter.emit(ev);
		this._sessionFinishedResolve(this._currentUser);
	}
	
	private _log(message:string):void {
		if (UserManager.SHOW_DEBUG_MESSAGES){
			console.log(message);
		}
	}
	
	public get currentUser():AppUser { return this._currentUser; }
	
	public get userId():number { return this._currentUser?.userId; }
	
	public get userSignedIn():boolean { return this._currentUser != null; }
	
	get adminSignedIn():boolean { 
		return (this._currentUser != null &&
			this._currentUser.role === UserRole.ADMIN);
	}
	
	public get communityMembers():Array<UserDTO> {
		return this._communityMembers;
	}
}

export class UserManagerEvent {
	public type:UserManagerEventType;
	
	constructor(type:UserManagerEventType){
		this.type = type;
	}
}

export enum UserManagerEventType {
	/**dispatched when user logs in/out (also at app launch)*/
	authStateChanged,
	
	/**dispatched when operations are complete and the UI may be removed. For
	 * example, the user may have clicked Cancel or Close in one of the views.
	 * Check the [FbUserManager.currentUser] getter to get the [AppUser] or 
	 * check if its null.*/
	sessionFinished
}